import React from 'react'
import { supabase } from '@/lib/supabase'
import { applyOptimisticReactionToggle } from '@/shared/utils/reactions'
import {
  fetchCommunityThread,
  postCommunityMessage,
  toggleCardReaction,
  toggleMessageReaction,
  deleteCommunityMessage,
  type CommunityThread,
  addCommunityMessageAttachments,
  updateCommunityMessage,
} from '../services/communityService'
import { uploadPublicFile } from '@/modules/upload/services/uploadService'
import { validateCommunityAttachmentFile } from '../utils/communityAttachments'
import {
  appendEditedSuffixToHtml,
  prepareCommunityMessageContent,
  stripEditedSuffixFromHtml,
} from '../utils/communityMessageContent'
import { emitCommunityThreadNotificationsAutoEnabled } from '../services/communityThreadNotificationsEvents'

export function useCommunityThread(
  opportunityId: number | null,
  currentUser: { name: string; avatarUrl?: string | null } | null,
  active: boolean,
) {
  const [loading, setLoading] = React.useState(false)
  const [sending, setSending] = React.useState(false)
  const [loadedOnce, setLoadedOnce] = React.useState(false)
  const [thread, setThread] = React.useState<CommunityThread>({
    messages: [],
    cardReactions: [],
  })

  const load = React.useCallback(async () => {
    if (!opportunityId || !active) {
      setThread({ messages: [], cardReactions: [] })
      setLoadedOnce(false)
      return
    }

    setLoading(true)
    try {
      const data = await fetchCommunityThread(opportunityId)
      setThread((prev) => {
        const byId: Record<string, (typeof prev.messages)[number]> = {}

        // Fonte de verdade: mensagens vindas do servidor
        ;(data.messages || []).forEach((msg) => {
          byId[String(msg.id)] = msg
        })

        // Mantém apenas mensagens otimistas locais (ids negativos)
        prev.messages.forEach((msg) => {
          if (typeof msg.id === 'number' && msg.id < 0) {
            const key = String(msg.id)
            if (!byId[key]) {
              byId[key] = msg
            }
          }
        })

        const merged = Object.values(byId).sort((a, b) => {
          const ta = new Date(a.createdAt || '').getTime()
          const tb = new Date(b.createdAt || '').getTime()
          return ta - tb
        })
        return {
          messages: merged,
          cardReactions: data.cardReactions,
        }
      })
      setLoadedOnce(true)
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('useCommunityThread load error', error)
    } finally {
      setLoading(false)
    }
  }, [active, opportunityId])

  React.useEffect(() => {
    if (!opportunityId || !active) {
      setThread({ messages: [], cardReactions: [] })
      setLoadedOnce(false)
      return
    }

    let alive = true

    ;(async () => {
      await load()
      if (!alive) return
    })()

    return () => {
      alive = false
    }
  }, [active, load, opportunityId])

  // Realtime: refletir interações com emojis (card + mensagens) em tempo real
  React.useEffect(() => {
    if (!opportunityId || !active) return

    let timer: any = null

    const scheduleReload = () => {
      try {
        if (timer) clearTimeout(timer)
      } catch {
        // ignore
      }
      timer = setTimeout(() => {
        load().catch(() => {
          // erros são tratados dentro de load
        })
      }, 500)
    }

    const channel = supabase
      .channel(`community_thread:${opportunityId}`)
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'opportunity_community_message_reactions' },
        () => {
          // Qualquer alteração de reaction em mensagem dispara um reload suave
          scheduleReload()
        },
      )
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'opportunity_community_reactions',
          filter: `opportunity_id=eq.${opportunityId}`,
        } as any,
        () => {
          scheduleReload()
        },
      )
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'opportunity_community_messages',
          filter: `opportunity_id=eq.${opportunityId}`,
        } as any,
        () => {
          // Novas mensagens ou edições também recarregam o thread
          scheduleReload()
        },
      )
      .subscribe()

    return () => {
      try {
        supabase.removeChannel(channel)
      } catch {
        // ignore
      }
      try {
        if (timer) clearTimeout(timer)
      } catch {
        // ignore
      }
    }
  }, [active, load, opportunityId])

  // Polling adicional para garantir que novas mensagens e reações
  // apareçam para todos os usuários mesmo se o canal realtime falhar.
  React.useEffect(() => {
    if (!opportunityId || !active) return

    const interval = setInterval(() => {
      load().catch(() => {
        // silencioso; próximo ciclo corrige
      })
    }, 1000)

    return () => {
      clearInterval(interval)
    }
  }, [active, load, opportunityId])

  const sendMessage = React.useCallback(
    async (content: string, attachments?: File[]) => {
      if (!opportunityId || !currentUser || !active) return

      const prepared = prepareCommunityMessageContent(String(content || ''))
      const html = prepared.html
      const files = (Array.isArray(attachments) ? attachments : [])
        .filter((file): file is File => !!file)
        .filter((file) => validateCommunityAttachmentFile(file).ok)

      if (!prepared.text && !files.length) return

      // Mensagem otimista local para o remetente ver imediatamente
      const tempId = Date.now() * -1
      const createdAt = new Date().toISOString()

      setThread((prev) => ({
        ...prev,
        messages: [
          ...prev.messages,
          {
            id: tempId,
            opportunityId,
             authorId: 'me',
             authorName: currentUser.name,
             authorAvatarUrl: currentUser.avatarUrl ?? null,
             content: html,
             createdAt,
             reactions: [],
             attachments: [],
           },
         ],
      }))

      setSending(true)
      try {
        const saved = await postCommunityMessage(
          opportunityId,
          html,
          currentUser.name,
          currentUser.avatarUrl ?? null,
        )

        if (saved) {
          emitCommunityThreadNotificationsAutoEnabled({
            contextType: 'opportunity',
            contextId: opportunityId,
          })
        }

        if (saved && files.length > 0) {
          const uploaded: {
            file: File
            url: string
            bucket: string
            path: string
          }[] = []

          for (const file of files) {
            try {
              const { url, bucket, path } = await uploadPublicFile(
                file,
                `community/messages/${saved.id}`,
              )
              uploaded.push({ file, url, bucket, path })
            } catch (uploadErr) {
              // eslint-disable-next-line no-console
              console.error('[Community] upload attachment error', uploadErr)
            }
          }

          if (uploaded.length > 0) {
            try {
              await addCommunityMessageAttachments(
                saved.id,
                uploaded.map((item) => ({
                  url: item.url,
                  storageBucket: item.bucket,
                  storagePath: item.path,
                  contentType: item.file.type || null,
                  sizeBytes:
                    typeof item.file.size === 'number' ? item.file.size : null,
                })),
              )
              await load()
            } catch (attachErr) {
              // eslint-disable-next-line no-console
              console.error('[Community] add attachments error', attachErr)
            }
          }
        }

        if (saved) {
          // Substitui a mensagem temporária pela versão oficial do servidor
          setThread((prev) => ({
            ...prev,
            messages: prev.messages.map((msg) => (msg.id === tempId ? saved : msg)),
          }))
        } else {
          // Se não vier nada, força um reload completo
          await load()
        }
      } finally {
        setSending(false)
      }
    },
    [active, currentUser, load, opportunityId],
  )

  const toggleMessageEmoji = React.useCallback(
    async (messageId: number, emoji: string) => {
      if (!opportunityId || !active) return

      const trimmed = String(emoji || '').trim()
      if (!trimmed) return

      const id = Number(messageId)
      if (!Number.isFinite(id) || id <= 0) return

      // eslint-disable-next-line no-console
      console.log('[Community] toggleMessageEmoji', {
        opportunityId,
        messageId,
        emoji: trimmed,
      })

      setThread((prev) => ({
        ...prev,
        messages: prev.messages.map((msg) =>
          Number(msg.id) === id
            ? {
                ...msg,
                reactions: applyOptimisticReactionToggle(msg.reactions, trimmed),
              }
            : msg,
        ),
      }))

      void toggleMessageReaction(id, trimmed).catch((error) => {
        // eslint-disable-next-line no-console
        console.error('[Community] toggleMessageReaction error', error)
        load().catch(() => {
          // erro já é tratado dentro de load
        })
      })
    },
    [active, load, opportunityId],
  )

  const toggleCardEmoji = React.useCallback(
    async (emoji: string) => {
      if (!opportunityId || !active) return

      const trimmed = String(emoji || '').trim()
      if (!trimmed) return

      setThread((prev) => ({
        ...prev,
        cardReactions: applyOptimisticReactionToggle(prev.cardReactions, trimmed),
      }))

      void toggleCardReaction(opportunityId, trimmed).catch((error) => {
        // eslint-disable-next-line no-console
        console.error('[Community] toggleCardReaction error', error)
        load().catch(() => {
          // erro já é tratado dentro de load
        })
      })
    },
    [active, load, opportunityId],
  )

  const deleteMessage = React.useCallback(
    async (messageId: number) => {
      if (!opportunityId || !active) return
      const id = Number(messageId)
      if (!Number.isFinite(id)) return

      // Remoção otimista local para feedback imediato
      setThread((prev) => ({
        ...prev,
        messages: prev.messages.filter((msg) => Number(msg.id) !== id),
      }))

      try {
        await deleteCommunityMessage(id)
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('[Community] deleteMessage error', error)
      } finally {
        // Garante sincronização com o estado real (inclui outros clientes via realtime)
        load().catch(() => {
          // erro já é tratado dentro de load
        })
      }
    },
    [active, load, opportunityId],
  )

  const editMessage = React.useCallback(
    async (messageId: number, content: string) => {
      if (!opportunityId || !active) return

      const prepared = prepareCommunityMessageContent(String(content || ''))
      if (!prepared.text) return

      const baseHtml = stripEditedSuffixFromHtml(prepared.html).html
      const finalHtml = appendEditedSuffixToHtml(baseHtml)
      if (!finalHtml) return

      try {
        const updated = await updateCommunityMessage(messageId, finalHtml)
        if (!updated) {
          await load()
          return
        }

        setThread((prev) => ({
          ...prev,
          messages: prev.messages.map((msg) =>
            msg.id === messageId ? { ...msg, content: updated.content } : msg,
          ),
        }))
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('[Community] editMessage error', error)
        await load()
      }
    },
    [active, load, opportunityId],
  )

  return {
    loading,
    loadedOnce,
    sending,
    thread,
    sendMessage,
    toggleMessageEmoji,
    toggleCardEmoji,
    deleteMessage,
    editMessage,
    reload: load,
  }
}
